import { Box } from '@mui/material';
import { useDrop } from 'react-dnd';
import { useEffect, useState } from 'react';
import {
  addCalculationField,
  addSource,
  insertFlowSource,
  setSource,
} from '../../../redux/Calculations/CalculationsActions';
import ShortUniqueId from 'short-unique-id';
// import { ICalculationSession } from "../../../redux/_calculations/CurrentCalculationSessionInterface";
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { isFlowFilledProperly, isFlowAlreadyPresentInStep } from '../utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { useDispatch } from 'react-redux';
interface IDropZone {
  children: React.ReactNode;
  propKey: string;
  flow: string;
  sourceLength?: number;
  identifier?: string;
  disableDrop?: boolean;
  sx?: React.CSSProperties;
}
const DateDropZone = ({
  children,
  propKey,
  flow,
  sourceLength,
  identifier,
  disableDrop = false,
  sx,
}: IDropZone) => {
  const [showWarning, setShowWarning] = useState(false);
  const [showBorder, setShowBorder] = useState(false);

  const uid = new ShortUniqueId({ length: 4 });
  const _calculations = useSelector((state: RootState) => state.calculations);
  const dispatch = useDispatch();

  const [alert, setAlert] = useState<any>(null);

  const activeFlow = _calculations.properties[propKey]?.currentCalculationSession?.activeFlow!;

  const isDropNotAllowed = (draggedFlowName: string, activeFlowName: string): boolean => {
    return draggedFlowName === activeFlowName;
  };

  const currFlow =
    _calculations?.properties[propKey]?.currentCalculationSession?.calculationInfo.flows;

  // const doesSourceArrayContainFieldOrFlow = sourceTypes?.includes("field") || sourceTypes?.includes("flow")

  const [{ isOver }, drop] = useDrop({
    accept: ['card', 'calculationSelected'],
    hover: (item: any, monitor) => {
      if (monitor.isOver({ shallow: true }) && !showBorder) {
        setShowBorder(true);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (item: any, monitor) => {
      if (disableDrop) {
        setAlert({
          severity: 'warning',
          message:
            'This calculation is being used in another flow. Please remove the dependency before deleting.',
        });
        return;
      }
      setShowBorder(false);
      const calculationInfo =
        _calculations.properties[propKey]?.currentCalculationSession?.calculationInfo;
      if (!calculationInfo || !currFlow) return;

      if (isDropNotAllowed(item.uid, activeFlow)) {
        setAlert({
          severity: 'warning',
          message: 'Same flow can not be dropped in itself',
        });
        return;
      }
      if (item.type === 'calculationSelected') {
        const isAgggregated =
          _calculations.properties[propKey]?.currentCalculationSession?.flowAggregations[
            activeFlow
          ];
        if (isAgggregated === null || isAgggregated === undefined) {
          setAlert({
            severity: 'warning',
            message: 'Please select an aggregation type for this flow before dropping a source',
          });
        }
        const droppedItemAggregationType = item.fieldData.isAggregated;
        if (
          (isAgggregated && !droppedItemAggregationType) ||
          (droppedItemAggregationType && !isAgggregated)
        ) {
          setAlert({
            severity: 'warning',
            message: 'Cannot drop a non-aggregated flow into an aggregated flow and vice versa',
          });
          return;
        }
      }
      if (item.type === 'calculationSelected') {
        if (!isFlowFilledProperly(calculationInfo, item.uid)) {
          setAlert({
            severity: 'warning',
            message: `Please ensure at least one field or flow is present and all conditions are met for flow ${item.name} before dropping.`,
          });
          return;
        }
        if (isFlowAlreadyPresentInStep(calculationInfo, item.uid, activeFlow, 0, 'source')) {
          const droppedFlowName =
            _calculations.properties[propKey]?.currentCalculationSession?.flowDisplayNames[
              item.uid
            ];
          const parentFlowName =
            _calculations.properties[propKey]?.currentCalculationSession?.flowDisplayNames[
              activeFlow
            ];
          setAlert({
            severity: 'warning',
            message: `${droppedFlowName} is already present inside ${parentFlowName}.`,
          });
          return;
        }
      }

      if (item.type === 'card') {
        if (!item.fieldData) return;

        if (
          flow !== 'stringToDate' &&
          !['date', 'timestamp'].includes(item.fieldData.dataType.toLowerCase())
        ) {
          setShowWarning(true);
          return;
        } else if (flow === 'stringToDate') {
          if (
            ![
              'INT',
              'INTEGER',
              'BIGINT',
              'SMALLINT',
              'TINYINT',
              'FLOAT',
              'REAL',
              'DOUBLE',
              'DECIMAL',
              'NUMERIC',
              'DOUBLE PRECISION',
              'CHAR',
              'VARCHAR',
              'TEXT',
              'STRING',
            ].includes(item.fieldData.dataType.toUpperCase())
          ) {
            setShowWarning(true);
            return;
          }
        }

        const fieldId = uid();
        dispatch(
          addCalculationField(propKey, fieldId, {
            tableId: item.fieldData.tableId,
            fieldName: item.fieldData.fieldname,
            displayName: item.fieldData.displayname,
            dataType: item.fieldData.dataType,
          }),
        );
        if (flow === 'addDateInterval') {
          dispatch(setSource(propKey, activeFlow, 0, fieldId, 'field', 0));
          dispatch(setSource(propKey, activeFlow, 0, 3, 'integer', 1));
          dispatch(setSource(propKey, activeFlow, 0, 'day', 'text', 2));
        } else if (flow === 'truncateDate') {
          dispatch(setSource(propKey, activeFlow, 0, fieldId, 'field', 0));
          dispatch(setSource(propKey, activeFlow, 0, 'year', 'text', 1));
        } else if (['datePartName', 'datePartNumber'].includes(flow)) {
          dispatch(setSource(propKey, activeFlow, 0, fieldId, 'field', 0));
          dispatch(setSource(propKey, activeFlow, 0, 'month', 'text', 1));
        } else if (['minDate', 'maxDate'].includes(flow)) {
          dispatch(setSource(propKey, activeFlow, 0, fieldId, 'field', 0));
        } else if (flow === 'dateInterval') {
          /**
           * Date Difference
           */
          if (!sourceLength && sourceLength !== 0) return;

          if (identifier === 'from') {
            dispatch(addSource(propKey, activeFlow, 0, fieldId, 'field', 0));
            dispatch(addSource(propKey, activeFlow, 0, 'day', 'text', 2));
          } else if (identifier === 'to') {
            dispatch(addSource(propKey, activeFlow, 0, fieldId, 'field', 1));
          } else {
            dispatch(setSource(propKey, activeFlow, 0, fieldId, 'field', 1));
          }
        } else if (flow === 'stringToDate') {
          dispatch(addSource(propKey, activeFlow, 0, fieldId, 'field', 0));
        }
      } else if (item.type === 'calculationSelected') {
        const currentCalculationSession =
          _calculations.properties[propKey]?.currentCalculationSession;

        const activeConditionIndex = currentCalculationSession?.activeCondition;

        if (activeConditionIndex !== 0 && !activeConditionIndex) return;

        const flowResultantDatatype =
          currentCalculationSession?.calculationInfo?.flows[item.uid][0]?.resultantDatatype;

        if (!flowResultantDatatype) return;
        if (
          flow !== 'stringToDate' &&
          !['date', 'timestamp'].includes(flowResultantDatatype.toLocaleLowerCase())
        ) {
          setShowWarning(true);
          return;
        } else if (flow === 'stringToDate') {
          if (
            ![
              'INT',
              'INTEGER',
              'BIGINT',
              'SMALLINT',
              'TINYINT',
              'FLOAT',
              'REAL',
              'DOUBLE',
              'DECIMAL',
              'NUMERIC',
              'DOUBLE PRECISION',
              'CHAR',
              'VARCHAR',
              'TEXT',
              'STRING',
            ].includes(flowResultantDatatype.toUpperCase())
          ) {
            setShowWarning(true);
            return;
          }
        }

        if (flow === 'addDateInterval') {
          dispatch(insertFlowSource(propKey, activeFlow, item.uid));
          dispatch(setSource(propKey, activeFlow, 0, 3, 'integer', 1));
          dispatch(setSource(propKey, activeFlow, 0, 'day', 'text', 2));
        } else if (flow === 'dateInterval') {
          if (!sourceLength && sourceLength !== 0) return;
          if (identifier === 'from') {
            dispatch(addSource(propKey, activeFlow, 0, item.uid, 'flow', 0));
            dispatch(addSource(propKey, activeFlow, 0, 'day', 'text', 2));
          } else if (identifier === 'to') {
            dispatch(addSource(propKey, activeFlow, 0, item.uid, 'flow', 1));
          } else {
            dispatch(setSource(propKey, activeFlow, 0, item.uid, 'flow', 1));
          }
        } else if (flow === 'truncateDate') {
          dispatch(insertFlowSource(propKey, activeFlow, item.uid));
          dispatch(setSource(propKey, activeFlow, 0, 'year', 'text', 1));
        } else if (['datePartName', 'datePartNumber'].includes(flow)) {
          dispatch(insertFlowSource(propKey, activeFlow, item.uid));
          dispatch(setSource(propKey, activeFlow, 0, 'month', 'text', 1));
        } else if (['minDate', 'maxDate'].includes(flow)) {
          dispatch(insertFlowSource(propKey, activeFlow, item.uid));
        } else if (flow === 'stringToDate') {
          dispatch(insertFlowSource(propKey, activeFlow, item.uid));
        }
      }
    },
  });

  useEffect(() => {
    if (!isOver) {
      setShowBorder(false);
    }
  }, [isOver]);

  return (
    <>
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      <Box
        ref={drop}
        sx={{
          cursor: 'pointer',
          textAlign: 'left',
          paddingBottom: '10px',
          paddingTop: '5px',
          borderBottom: '1px solid  #999999',
          height: 'auto',
          marginBottom: '0.5rem',
          border: '1px solid transparent',
          flexGrow: 1,
          ...sx,
        }}
        className={`dropzone ${showBorder ? 'dropzone-highlighted' : ''}`}
      >
        {children}
      </Box>
      <NotificationDialog
        openAlert={showWarning}
        onCloseAlert={() => setShowWarning(false)}
        severity="warning"
        testMessage={
          flow === 'stringToDate'
            ? 'Only string or number types are allowed'
            : 'Only date  or date related fields are allowed'
        }
      />
    </>
  );
};

export default DateDropZone;
